import { Controller } from "@hotwired/stimulus"

// Learner Mini Quiz: one question at a time, marked as soon as the learner
// commits to an answer — a single-answer question on selection, a multiple
// answer question on "Check answer" — revealing the author's correct or wrong
// feedback underneath. Previous/Next steps through the questions and answers
// can be changed at any time. All state is client-side only: the in-lesson
// Mini Quiz is practice, so nothing is submitted or persisted.
export default class extends Controller {
  static targets = ["question", "option", "progressLabel", "progressFill", "previousButton", "nextButton"]

  answer(event) {
    const question = event.currentTarget.closest("[data-mini-quiz-target='question']")
    if (!question) return

    const selectedIds = this.selectedOptionIdsFor(question)
    if (selectedIds.length === 0) return

    const correctIds = this.correctOptionIdsFor(question)
    const correct = selectedIds.length === correctIds.length && selectedIds.every((id) => correctIds.includes(id))

    this.markOptions(question, selectedIds, correctIds)
    this.showFeedback(question, correct ? "correct" : "wrong", selectedIds)
  }

  // Only what the learner picked is marked: a right pick greens, a wrong pick
  // reds. The answers they missed stay blank whichever way the question is
  // answered, so a failed attempt reads as unfinished instead of handing over the
  // answer key on the first try — the same lesson for a single pick as for a
  // multiple-answer tick.
  markOptions(question, selectedIds, correctIds) {
    this.optionsFor(question).forEach((option) => {
      const optionId = option.dataset.optionId
      const isCorrect = correctIds.includes(optionId)
      const picked = selectedIds.includes(optionId)

      option.classList.toggle("is-correct", picked && isCorrect)
      option.classList.toggle("is-wrong", picked && !isCorrect)
    })
  }

  // A multiple-answer question is only marked once the learner presses "Check
  // answer", so ticking another box drops the previous verdict until they do.
  clearMarks(event) {
    const question = event.currentTarget.closest("[data-mini-quiz-target='question']")
    if (!question) return

    this.optionsFor(question).forEach((option) => option.classList.remove("is-correct", "is-wrong"))
    this.showFeedback(question, null)
  }

  previousQuestion(event) {
    event.preventDefault()
    this.showQuestion(this.currentIndex - 1)
  }

  nextQuestion(event) {
    event.preventDefault()
    this.showQuestion(this.currentIndex + 1)
  }

  showQuestion(index) {
    const total = this.questionTargets.length
    if (index < 0 || index >= total) return

    this.questionTargets.forEach((question) => {
      question.hidden = Number(question.dataset.questionIndex) !== index
    })

    this.progressLabelTarget.textContent = `Question ${index + 1} of ${total}`
    this.progressFillTarget.style.width = `${((index + 1) / total) * 100}%`
    if (this.hasPreviousButtonTarget) this.previousButtonTarget.disabled = index === 0
    if (this.hasNextButtonTarget) this.nextButtonTarget.disabled = index >= total - 1
  }

  showFeedback(question, kind, selectedIds = []) {
    let revealed = null

    question.querySelectorAll("[data-mini-quiz-feedback]").forEach((feedback) => {
      feedback.hidden = feedback.dataset.miniQuizFeedback !== kind
      if (!feedback.hidden) revealed = feedback
    })

    this.showOptionFeedback(question, selectedIds)

    if (revealed) this.scrollIntoViewIfNeeded(revealed)
  }

  // Where the author wrote a rationale per option, only the ones the learner
  // actually picked are revealed: the rest stay hidden so a wrong attempt doesn't
  // hand over the reasoning for every other answer.
  showOptionFeedback(question, selectedIds) {
    question.querySelectorAll("[data-mini-quiz-option-feedback]").forEach((rationale) => {
      rationale.hidden = !selectedIds.includes(rationale.dataset.miniQuizOptionFeedback)
    })
  }

  // The verdict sits under the options, so on a long question it opens below the
  // fold and the learner reads the marks as a pass. Only scrolled when it is
  // actually off screen, so an already-visible panel never jumps the page.
  scrollIntoViewIfNeeded(element) {
    const rect = element.getBoundingClientRect()
    if (rect.top >= 0 && rect.bottom <= window.innerHeight) return

    // Centred, not "nearest": nearest parks the panel flush against the fold,
    // where a fractional scroll offset leaves its last fraction of a pixel cut
    // off. Centring gives the verdict room on both sides and can't round away.
    element.scrollIntoView({ block: "center" })
  }

  optionsFor(question) {
    return this.optionTargets.filter((option) => question.contains(option))
  }

  selectedOptionIdsFor(question) {
    return this.optionsFor(question)
      .filter((option) => option.querySelector(".academy-quiz-option-input")?.checked)
      .map((option) => option.dataset.optionId)
  }

  correctOptionIdsFor(question) {
    return JSON.parse(question.dataset.correctOptionIds || "[]").map(String)
  }

  get currentIndex() {
    const visible = this.questionTargets.find((question) => !question.hidden)

    return visible ? Number(visible.dataset.questionIndex) : 0
  }
}
